import type { Activity, CreateActivityInput, WorkerStatus } from '../api/types';
import type { CreatableActivityWorkPath } from '@internal/spassu-code-contract';

export type PortalActivityWorkPath = CreatableActivityWorkPath;

/**
 * Copy de uma via na tela de escolha e no formulário seguinte. Fonte única:
 * divergência de texto entre as duas telas fica impossível por construção.
 */
export interface ActivityWorkPathCopy {
  readonly title: string;
  readonly description: string;
  readonly prerequisites: readonly string[];
  readonly consequence: string;
}

export interface ActivityCreationJourney {
  workPath: PortalActivityWorkPath;
  title: string;
  description: string;
  prerequisites: readonly string[];
  consequence: string;
  showAiEngine: boolean;
  requireTargetBranch: boolean;
  branchLabel: string;
  onCreated(activity: Activity): void;
}

export interface ActivityCreationGateway {
  create(input: CreateActivityInput): Promise<Activity>;
  workerStatus(): Promise<WorkerStatus>;
}

export interface ActivityCreationResult {
  activity: Activity;
  workPath: PortalActivityWorkPath;
}

export const FRONTIER_CREATION_COPY = {
  title: 'Frontier Web',
  description: 'Fluxo web completo, com Motor de IA e clone automático.',
  prerequisites: [
    'Worker local conectado para clonar e executar o pipeline.',
    'Acesso ao repositório e ao projeto selecionados.',
  ],
  consequence:
    'A atividade seguirá o pipeline Frontier e a via não poderá ser alterada depois da criação.',
} as const;

export const CONTROL_PLANE_CREATION_COPY = {
  title: 'Control Plane',
  description: 'Fluxo para conectar um agente e executar o trabalho local.',
  prerequisites: [
    'Acesso ao repositório remoto selecionado.',
    'Branch base/destino do PR existente no repositório remoto.',
    'No Windows, use o Git Bash para executar os comandos do Control Plane.',
  ],
  consequence:
    'Na próxima tela, gere o pareamento para clonar o repositório em um workspace exclusivo e conectar o agente. O PR será aberto contra a branch escolhida e a via não poderá ser alterada depois da criação.',
} as const;

/**
 * Catálogo de copy por via criável. O `satisfies Record<CreatableActivityWorkPath, ...>`
 * é o que transforma "via criável nova sem copy" em erro de compilação em vez de
 * opção invisível na tela (ADR-005, US-004.AC-4).
 */
export const ACTIVITY_WORK_PATH_COPY = {
  'frontier-web': FRONTIER_CREATION_COPY,
  'control-plane': CONTROL_PLANE_CREATION_COPY,
} as const satisfies Record<CreatableActivityWorkPath, ActivityWorkPathCopy>;
